import { cloneDeep } from 'lodash-es';
import { PC_CONSOLE_GRANULARITIES_METRICS_MAPPING } from '@/pages/intelligence/pages/TopCharts/_config/metricOptions';
import { analysisDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import {
  TopchartPageId,
  MSCIENCE_METRICS_REGIONS,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { CONSOLE_AGGREGATED_DEVICES } from '@intelligence/pages/TopCharts/PCGames/_config/constant';

import type { IDateTypely } from '@intelligence/types/common';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import type { PCConsolePageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/type';
import type { TopchartFilterCfgType } from '@intelligence/types/topchart';

/**
 * 根据当前勾选的国家地区 判断当前指标在当前勾选的国家地区条件下 是否有效
 * 注意这里metricCategory是 指标的分类，而不是指标的数据源，但是判断依据还是根据指标分类对应的数据源 来判断的
 * @param code 指标code 格式为 指标分类__指标key（分隔符只是个参照，实际分割符 是通过 公共方法提供的，只需要调用analysisDataSourceMetricKey方法解析 就可以获取到 分类、指标key）
 * @param regions 当前勾选的国家地区
 * @returns 当前指标在当前勾选的国家地区条件下 是否有效
 */
const isMetricRegionsValid = (code: string, regions: string[]): boolean => {
  // console.log('isMetricRegionsValid-code', code);
  // console.log('isMetricRegionsValid-regions', regions);
  if (!code) return true;
  if (!Array.isArray(regions)) return true;
  const regionsTmp = regions?.map((item: string) => item?.toLowerCase()) || [];
  const { dataSource: metricCategory, metric } = analysisDataSourceMetricKey(code);
  // newzoo的指标
  if (
    ['mau', 'engagement', 'churnAcquisition', 'timeSpent', 'retention', 'noUpdate'].includes(
      metricCategory,
    )
  )
    return true;
  const isGlobal = regionsTmp?.includes('global');
  // mscience的指标
  if (
    metricCategory === 'revenue' &&
    [
      'digitalRevenue',
      'units',
      'ast',
      'mscienceLifetimeDigitalRevenue',
      'mscienceLifetimeDigitalUnits',
    ].includes(metric)
  ) {
    return isGlobal || regionsTmp?.some((region) => MSCIENCE_METRICS_REGIONS.includes(region));
  }
  if (
    // mscience的指标
    (metricCategory === 'revenue' && metric === 'digitalARPPU') ||
    // npd的physicalRevenue指标
    (metricCategory === 'revenue' && metric === 'revenue') ||
    (metricCategory === 'revenue' && metric === 'physical_units')
  ) {
    return isGlobal || regionsTmp?.includes('us');
  }
  // databrain的指标：仅支持 global
  if (metricCategory === 'revenue' && metric?.startsWith('databrain__')) return isGlobal;
  // famitsu的指标（console japan sales）
  if (metricCategory === 'japanSales') return isGlobal || regionsTmp?.includes('jp');
  // Steam Data / Gamalytic / Alinea / Console Data：仅支持 global
  if (
    metricCategory === 'steamData' ||
    metricCategory === 'gamalytic' ||
    metricCategory === 'alinea' ||
    metricCategory === 'consoleData'
  ) return isGlobal;
  return true;
};

const isMetricDevicesValid = (code: string, devices: string[]): boolean => {
  if (!code) return true;
  if (!Array.isArray(devices)) return true;
  const { dataSource: metricCategory } = analysisDataSourceMetricKey(code);
  if (!['steamData', 'japanSales'].includes(metricCategory)) return true;
  // 暂时取消device禁选限制
  // if (metricCategory === 'steamData') return devices?.includes('PC');
  // if (metricCategory === 'japanSales') {
  //   return devices?.some((device) => CONSOLE_AGGREGATED_DEVICES.includes(device));
  // }
  return true;
};

export const getCascaderMetricsOptionsAndValue = (
  pageId: PCConsolePageId,
  options: OptionsItemType[],
  value: string[],
  fetchParams: {
    date_type: IDateTypely;
    regions: string[];
    device: string[];
    start_date: string;
    end_date: string;
  },
  // 过滤掉禁用项的值，默认逻辑，pc&console 有自定义修改宽表指标时，不需要过滤重置
  filterOutDisabledValue = true,
  preservedValues: string[] = [],
): {
  options: OptionsItemType[];
  value: string[];
  disabledValue: string[];
} => {
  const allowPageIds = [
    TopchartPageId.PCConsoleMetricsTable,
    TopchartPageId.PCConsoleDataBrainCalibration,
  ] as PCConsolePageId[];
  if (!allowPageIds.includes(pageId) || !Array.isArray(options) || !Array.isArray(value)) {
    return {
      options: [],
      value: [],
      disabledValue: [],
    };
  }

  const optionsTmp = cloneDeep(options);
  const granularity = fetchParams?.date_type || 'daily';
  const regions = fetchParams?.regions || ['global'];
  const devices = fetchParams?.device || [];
  const granularityMetrics = PC_CONSOLE_GRANULARITIES_METRICS_MAPPING[granularity];
  const preservedValueSet = new Set(preservedValues);
  // const allMetrics: string[] = [];
  const availableMetrics: string[] = [];
  // const disabledMetrics: string[] = [];
  let valueTmp: string[] = [];
  let disabledValue: string[] = [];

  for (const optionTmp of optionsTmp) {
    const childrenTmp = optionTmp?.children;
    // 当前分类 是否禁用
    let isOptionDisabled = true;
    if (!Array.isArray(childrenTmp)) continue;

    for (const childTmp of childrenTmp) {
      const childValue = childTmp?.value;
      // 指标在当前勾选地区下是否有效
      const isRegionValid = isMetricRegionsValid(childValue, regions);
      // 指标在当前时间粒度下是否有效
      const isGranularityValid = granularityMetrics?.includes(childValue);
      // 指标在当前device筛选条件下是否有效
      const isDevicesValid = isMetricDevicesValid(childValue, devices);
      const { metric } = analysisDataSourceMetricKey(childValue);
      // 是否是评分条件： 单天&global
      const storeRatingValid =
        metric === 'meta_score' &&
        granularity === 'daily' &&
        fetchParams?.start_date === fetchParams?.end_date &&
        regions?.[0] === 'global';
      childTmp.disabled = !(
        (isGranularityValid && isRegionValid && isDevicesValid) ||
        storeRatingValid
      );
      // console.log('childTmp', childTmp);
      // console.log('isGranularityValid', isGranularityValid);
      // console.log('isRegionValid', isRegionValid);
      // console.log('isDevicesValid', isDevicesValid);

      if (childTmp.disabled) {
        // disabledMetrics.push(childValue);
      } else {
        isOptionDisabled = false;
        childTmp.bubbleContent = '';
        childTmp.bubbleContentCN = '';
        availableMetrics.push(childValue);
      }
      // allMetrics.push(childValue);
    }
    optionTmp.disabled = isOptionDisabled;
    if (!optionTmp.disabled) {
      optionTmp.bubbleContent = '';
      optionTmp.bubbleContentCN = '';
    }
  }

  // valueTmp = disableValueFilter
  //   ? value
  //   : value?.filter((item) => availableMetrics?.includes(item));
  valueTmp = filterOutDisabledValue
    ? value?.filter((item) => availableMetrics?.includes(item) || preservedValueSet.has(item))
    : value;
  valueTmp = Array.from(new Set(valueTmp || []));
  // 禁用项
  disabledValue = value?.filter((item) => !availableMetrics?.includes(item));
  // 当前勾选的指标都被禁用时（已有勾选指标，然后修改筛选器，导致指标的禁用状态更新），默认选中可用的前八个指标
  if (!valueTmp?.length) {
    valueTmp = availableMetrics?.slice(0, Math.min(availableMetrics?.length, 8));
  }

  // 按照筛选器options中的分组顺序对valueTmp进行排序
  // 创建一个顺序映射，记录每个指标在options中的位置
  const orderMap = new Map<string, number>();
  let orderIndex = 0;
  for (const optionTmp of optionsTmp) {
    const childrenTmp = optionTmp?.children;
    if (!Array.isArray(childrenTmp)) continue;
    for (const childTmp of childrenTmp) {
      if (childTmp?.value) {
        orderMap.set(childTmp.value, orderIndex++);
      }
    }
  }
  // 根据orderMap对valueTmp排序
  valueTmp = valueTmp.sort((a, b) => {
    const orderA = orderMap.get(a) ?? Number.MAX_SAFE_INTEGER;
    const orderB = orderMap.get(b) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  return {
    options: optionsTmp,
    value: valueTmp || [],
    disabledValue,
  };
};

export const mergeFilterConfig = (
  filterCfg: TopchartFilterCfgType,
  configureTexts: Record<string, string>,
): TopchartFilterCfgType => {
  return {
    ...filterCfg,
    specialParams: {
      ...filterCfg?.specialParams,
      getRadioOptionsTips: () => {
        return { ...configureTexts };
      },
    },
  };
};
